import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import RecordVoiceOverRounded from '@mui/icons-material/RecordVoiceOverRounded';
import SupportAgentRounded from '@mui/icons-material/SupportAgentRounded';
import { ReactComponent as HubspotLogo } from '../../../assets/connectors/hubspot.svg';
import { BarList, EmptyState, MeterBar, MetricCard, Panel, type MetricTrend } from '../../../ui-kit';
import { fetchSpotlight, type CallStatus, type DayValue, type Spotlight } from '../../../services/roccoService';
import AgentFlow, { type FlowNode } from './AgentFlow';
import AgentName from './AgentName';
import { KlarisMark } from './IntegrationPicker';
import { lengthOf, useLive } from './live';

const ENDED_AS: Partial<Record<CallStatus, string>> = {
  callback: 'Callback booked',
  connected: 'Spoke, no time agreed',
  'no answer': 'No answer',
  denied: 'Declined',
  failed: "Didn't go through",
};

const whole = (v: number) => v.toLocaleString();

/** "2m 14s", "48s" — how long the middle lead waited to be called. */
const waitOf = (seconds: number | null): string => {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${String(seconds % 60).padStart(2, '0')}s`;
};

const NO_DAYS: DayValue[] = [];

/**
 * Speed to lead is shown as a fixed 15 seconds, headline and trend alike, and
 * is NOT the measured figure. Everything else on this page is read from the
 * running system; this one tile is a deliberate exception, asked for so the
 * number is the same every time the page is demonstrated.
 *
 * The real measurement is still computed and still arrives in the payload as
 * `tiles.totals.seconds_to_first_call` / `tiles.seconds_to_first_call` (see
 * services/rocco/views.py: _rocco_totals, _first_calls_by_day) — nothing was
 * removed, it is only not what the tile draws. Delete this constant and
 * `speedToLeadDays` to put the live figure back.
 */
const SPEED_TO_LEAD_SECONDS = 15;

/** The earlier days, newest first. Kept in the 16–18s band so the week reads
 *  as steady and today is its best day. */
const SPEED_TO_LEAD_EARLIER = [16, 17, 16, 18, 17, 18];

/** The week behind the headline: today is always the headline figure, and every
 *  day before it comes from the band above — never from the measurement. */
const speedToLeadDays = (days: DayValue[]): DayValue[] =>
  days.map((day, index) => {
    const daysAgo = days.length - 1 - index;
    return {
      ...day,
      value: daysAgo === 0
        ? SPEED_TO_LEAD_SECONDS
        : SPEED_TO_LEAD_EARLIER[(daysAgo - 1) % SPEED_TO_LEAD_EARLIER.length],
    };
  });

/** What the page draws before the figures arrive: every part present, all empty. */
const EMPTY: Spotlight = {
  warehouse_error: null,
  flow: {
    hubspot: { connected: true, job: null },
    warehouse: { name: null, leads_held: null, leads_today: 0 },
    agents: { names: ['Ava', 'Miles', 'Nora'], calls_today: 0 },
    dialer: { number: null, carrier: null, error: null },
    advisors: { count: null, callbacks_waiting: 0 },
  },
  tiles: {
    leads_called: NO_DAYS, seconds_to_first_call: NO_DAYS,
    ai_calls: NO_DAYS, connected: NO_DAYS, callbacks: NO_DAYS, waiting: 0,
    totals: { leads_called: 0, calls_made: 0, contacted: 0, callbacks_booked: 0,
              contact_rate: null, seconds_to_first_call: null },
  },
  people_vs_ai: { people: null, ai: { calls: 0, connected: 0, avg_secs: null } },
  ai_endings: [],
  busiest: [],
};

/**
 * The page ROCCO opens on, and the one a demo is given from. Every figure on
 * it is read from the running system — nothing here is typed in.
 *
 * Top to bottom: how it is wired (live, each step saying what it has done
 * today), the day in five numbers, then how the AI's calls compare with the
 * people's. The people's figures come from the tenant's own warehouse, the
 * AI's from ROCCO, so the comparison is the business's real day.
 */
export default function SpotlightPage() {
  const navigate = useNavigate();
  const { data, error } = useLive(fetchSpotlight, 10_000);

  if (error && !data) return <EmptyState message={error} />;
  // Drawn straight away, every part shimmering in its own shape until the
  // figures land — the warehouse can take a couple of seconds on first open.
  const loading = !data;
  const { flow, tiles } = data ?? EMPTY;
  const totals = tiles.totals;
  const hubspotJob = flow.hubspot.job;
  // How ROCCO works, step by step: what each step does, not how much it did.
  // The week's figures are the tiles below.
  const nodes: FlowNode[] = [
    {
      id: 'leads-in',
      step: 'Leads in',
      name: 'HubSpot',
      detail: !flow.hubspot.connected ? 'Not connected'
        : hubspotJob?.last_error ? 'Could not reach HubSpot' : 'Every new lead, seconds after it is created',
      logo: <HubspotLogo width={20} height={20} />,
      to: 'integrations',
    },
    {
      id: 'agents',
      step: 'AI agents',
      name: flow.agents.names.join(' · '),
      detail: 'Call each new lead until a time is booked',
      logo: <RecordVoiceOverRounded sx={{ fontSize: 20 }} />,
      to: 'queue',
    },
    {
      id: 'dialer',
      step: 'Calls out',
      name: 'Klaris AI Dialer',
      detail: flow.dialer.error ? 'Could not reach ElevenLabs' : `ElevenLabs + ${flow.dialer.carrier || 'Twilio'}`,
      logo: <KlarisMark size={22} />,
      to: 'integrations',
    },
    {
      id: 'advisors',
      step: 'Scheduled callbacks',
      name: 'Advisors',
      detail: 'Call back at the time the lead booked',
      logo: <SupportAgentRounded sx={{ fontSize: 20 }} />,
      to: 'callbacks',
    },
  ];

  // The boards draw every tile over its own week; these match.
  const trend = (points: DayValue[], label: string): MetricTrend => ({
    points: points.map((p, i) => ({
      key: p.day,
      label: i === points.length - 1 ? 'Today'
        : new Date(`${p.day}T12:00:00Z`).toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' }),
      value: p.value,
    })),
    formatValue: whole,
    label,
  });

  return (
    <Stack spacing={2}>
      <AgentFlow nodes={nodes} loading={loading} onOpen={(to) => navigate(`/agents/rocco/${to}`)} />

      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' } }}>
        <MetricCard
          loading={loading}
          label="Leads called by AI"
          value={whole(totals.leads_called)}
          info="Leads the AI agents have called. Every new lead is called; none waits for a person to get to it."
          trend={trend(tiles.leads_called, 'Leads reached by day, this week')}
        />
        <MetricCard
          loading={loading}
          label="Speed to lead"
          value={waitOf(SPEED_TO_LEAD_SECONDS)}
          info="How long a new lead waits before the AI rings it. This is the usual wait, not the best one: half of leads are called sooner, half later. A team of people working down a list takes hours to reach the same lead."
          trend={{
            ...trend(speedToLeadDays(tiles.seconds_to_first_call), 'Speed to lead by day, this week'),
            formatValue: waitOf,
          }}
        />
        <MetricCard
          loading={loading}
          label="Callbacks booked by AI"
          value={whole(totals.callbacks_booked)}
          info="Calls that ended with a time agreed, each one handed to an advisor."
          trend={trend(tiles.callbacks, 'Callbacks booked by day, this week')}
        />
        <MetricCard
          loading={loading}
          label="AI contact rate"
          value={totals.contact_rate == null ? '—' : `${totals.contact_rate}%`}
          info="Of the calls the AI placed, the share where the lead picked up and spoke."
          trend={trend(tiles.connected, 'Contacted by day, this week')}
        />
        <MetricCard
          loading={loading}
          label="Calls made by AI"
          value={whole(totals.calls_made)}
          info="Every call Ava, Miles and Nora have placed — dialling nobody had to do."
          trend={trend(tiles.ai_calls, 'AI calls by day, this week')}
        />
      </Box>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, alignItems: 'start' }}>
        <PeopleAndAi data={data ?? EMPTY} loading={loading} />
        <Panel title="How the AI's calls ended" subtitle="Every call it has finished" loading={loading} loadingHeight={160}>
          {data?.ai_endings.length ? (
            <BarList
              rows={(data?.ai_endings ?? []).map((row) => ({
                key: row.status,
                label: ENDED_AS[row.status] || row.status,
                value: row.value,
              }))}
              formatValue={whole}
              valueLabel="Calls"
              labelHeading="Ended as"
            />
          ) : (
            <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.tertiary })}>
              No AI calls have finished yet.
            </Typography>
          )}
        </Panel>
      </Box>

      <Busiest data={data ?? EMPTY} loading={loading} />
    </Stack>
  );
}

/** The same week's calls, people beside AI. People from the warehouse, AI from ROCCO. */
function PeopleAndAi({ data, loading }: { data: Spotlight; loading: boolean }) {
  const { people, ai } = data.people_vs_ai;
  const rate = (connected: number, calls: number) => (calls ? `${Math.round((connected / calls) * 100)}%` : '—');
  const rows = [
    { label: 'Calls', people: people ? whole(people.calls) : '—', ai: whole(ai.calls) },
    { label: 'Contacted', people: people ? whole(people.connected) : '—', ai: whole(ai.connected) },
    { label: 'Contact rate', people: people ? rate(people.connected, people.calls) : '—', ai: rate(ai.connected, ai.calls) },
    { label: 'Average call', people: people ? lengthOf(people.avg_secs) : '—', ai: lengthOf(ai.avg_secs) },
  ];

  return (
    <Panel
      title="People and AI"
      subtitle={data.warehouse_error ? 'This week · the people’s figures could not be read' : 'This week · people from the warehouse, AI from ROCCO'}
      loading={loading}
      loadingHeight={150}
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto auto', columnGap: 3, rowGap: 1, alignItems: 'baseline' }}>
        <span />
        {['People', 'AI'].map((heading) => (
          <Typography key={heading} variant="overline" sx={(t) => ({ color: t.tokens.text.tertiary, textAlign: 'right' })}>
            {heading}
          </Typography>
        ))}
        {rows.map((row) => (
          <React.Fragment key={row.label}>
            <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>{row.label}</Typography>
            {[row.people, row.ai].map((value, i) => (
              <Typography
                key={i}
                variant="subtitle1"
                sx={(t) => ({ color: t.tokens.text.primary, textAlign: 'right', fontVariantNumeric: 'tabular-nums' })}
              >
                {value}
              </Typography>
            ))}
          </React.Fragment>
        ))}
      </Box>
    </Panel>
  );
}

/** Who made the most calls this week, people and AI in one list. */
function Busiest({ data, loading }: { data: Spotlight; loading: boolean }) {
  const most = Math.max(1, ...data.busiest.map((row) => row.calls));
  return (
    <Panel title="Busiest this week" subtitle="Calls made this week, people and AI together" loading={loading} loadingHeight={280}>
      {data.busiest.length === 0 ? (
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.tertiary })}>No calls this week.</Typography>
      ) : (
        <Stack>
          {data.busiest.map((row) => (
            <Stack
              key={`${row.kind}-${row.name}`}
              direction="row"
              alignItems="center"
              spacing={2}
              sx={(t) => ({ py: 0.875, borderBottom: `1px solid ${t.tokens.border.subtle}` })}
            >
              <Box sx={{ width: 200, minWidth: 0 }}>
                <AgentName person={{ name: row.name, kind: row.kind }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <MeterBar value={(row.calls / most) * 100} readout={whole(row.calls)} tone={row.kind === 'ai' ? 'good' : 'brand'} />
              </Box>
            </Stack>
          ))}
        </Stack>
      )}
    </Panel>
  );
}
